import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

/**
 * Zusammenarbeit im Mail-Programm (14.10.2026, Marlene): offen sehen,
 * übergeben, Notizen am Verlauf. Die Regel, was „offen" ist, steht im Backend
 * (services/mail_offen.py) — dieselbe wie im Review.
 */

export type Ansicht = "offen" | "meine" | "wartet" | "kunde_wartet" | "alle"
export const ANSICHTEN: { key: Ansicht; label: string; titel: string }[] = [
  { key: "offen", label: "Offen", titel: "Im Posteingang, es muss noch jemand etwas tun (wie im Review) und niemand ist zuständig. Zugewiesene Mails liegen bei der Person. Mails mit Wiedervorlage in der Zukunft erscheinen erst an dem Tag wieder" },
  { key: "meine", label: "Meine", titel: "Offen und dir zugewiesen (Wiedervorlage in der Zukunft erscheint erst an dem Tag wieder)" },
  { key: "wartet", label: "Wartet auf Kunde", titel: "Wir haben geantwortet und erwarten etwas vom Kunden (Terminbestätigung, Fotos, Antwort auf eine Frage) — seitdem kam nichts, auch kein Anruf. Oder von Hand mit dem Knopf „Wartet auf Kunde“ gelegt, bis der Kunde antwortet" },
  // #93 (Paul 26.10.2026): nur eine zusätzliche Sicht — kein Alarm, nichts wird verschoben.
  { key: "kunde_wartet", label: "Kunde wartet", titel: "Der Plan sagt „Antworten“ oder „Aufgabe“, aber seit mehr als einem Werktag ist nichts passiert: nicht ausgeführt, nicht beantwortet (auch nicht in einem anderen Verlauf an denselben Kunden), nicht erledigt. „Gelesen“ zählt nicht als erledigt. Älteste zuerst" },
  { key: "alle", label: "Alle", titel: "Alle Mails im gewählten Ordner, ohne Filter" },
]

export type MailNotiz = {
  id: number
  autor?: string | null
  text: string
  todo: boolean
  todo_fuer?: string | null
  faellig_am?: string | null
  erledigt_am?: string | null
  erledigt_von?: string | null
  created_at: string
  email_id?: number | null
}

export type NotizenStand = {
  notizen: MailNotiz[]
  zustaendig?: string | null
  wiedervorlage_am?: string | null
  /** Von Hand auf „Wartet auf Kunde" gelegt, der Kunde hat noch nicht geantwortet (#191). */
  wartet_manuell?: boolean
  wartet_seit?: string | null
  wartet_von?: string | null
}

async function json<T>(r: Response): Promise<T> {
  if (!r.ok) {
    const d = await r.json().catch(() => ({}))
    throw new Error((d as { detail?: string }).detail || `HTTP ${r.status}`)
  }
  return r.json() as Promise<T>
}

/** Eine Person mit offenen, ihr zugewiesenen Mails (Personen-Filter, #198). */
export type PersonZahl = { name: string; n: number }

export function useAnsichtZahlen(tenant: string, postfach = "") {
  return useQuery({
    queryKey: ["mail-ansichten", tenant, postfach],
    queryFn: async () => json<{ offen: number; meine: number; wartet: number; kunde_wartet?: number; ich?: string | null; personen?: PersonZahl[] }>(
      await fetch(`/api/v1/emails/ansichten/zahlen?tenant=${encodeURIComponent(tenant || "")}${postfach ? `&postfach=${encodeURIComponent(postfach)}` : ""}`, { credentials: "include" })),
    refetchInterval: 60_000,
    staleTime: 20_000,
  })
}

/** Wer als zuständig gewählt werden kann — dieselbe Liste wie bei internen Aufgaben. */
export function useZustaendige() {
  return useQuery({
    queryKey: ["zustaendige"],
    queryFn: async () => (await json<{ items: string[] }>(
      await fetch("/api/v1/portal-aufgaben/zustaendige", { credentials: "include" }))).items,
    staleTime: 10 * 60_000,
  })
}

export function useNotizen(emailId: number) {
  return useQuery({
    queryKey: ["mail-notizen", emailId],
    queryFn: async () => json<NotizenStand>(
      await fetch(`/api/v1/emails/${emailId}/notizen`, { credentials: "include" })),
    enabled: !!emailId,
  })
}

/** Nach jeder Änderung: Liste, Zähler, Mail und Notizen neu — „offen" hängt an allem. */
export function useZusammenarbeitNeuLaden(emailId: number) {
  const qc = useQueryClient()
  return () => {
    qc.invalidateQueries({ queryKey: ["mail-notizen", emailId] })
    qc.invalidateQueries({ queryKey: ["email", String(emailId)] })
    qc.invalidateQueries({ queryKey: ["emails"] })
    qc.invalidateQueries({ queryKey: ["mail-ansichten"] })
  }
}

export function useNotizAnlegen(emailId: number) {
  const neu = useZusammenarbeitNeuLaden(emailId)
  return useMutation({
    mutationFn: async (v: { text: string; todo: boolean; todo_fuer?: string | null; faellig_am?: string | null }) =>
      json<MailNotiz>(await fetch(`/api/v1/emails/${emailId}/notizen`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(v),
      })),
    onSuccess: neu,
  })
}

export function useTodoAbhaken(emailId: number) {
  const neu = useZusammenarbeitNeuLaden(emailId)
  return useMutation({
    mutationFn: async (v: { id: number; erledigt: boolean }) =>
      json<MailNotiz>(await fetch(`/api/v1/emails/notizen/${v.id}`, {
        method: "PATCH", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ erledigt: v.erledigt }),
      })),
    onSuccess: neu,
  })
}

export function useNotizLoeschen(emailId: number) {
  const neu = useZusammenarbeitNeuLaden(emailId)
  return useMutation({
    mutationFn: async (id: number) =>
      json<{ geloescht: boolean }>(await fetch(`/api/v1/emails/notizen/${id}`, {
        method: "DELETE", credentials: "include",
      })),
    onSuccess: neu,
  })
}

export function useUebergeben(emailId: number) {
  const neu = useZusammenarbeitNeuLaden(emailId)
  return useMutation({
    mutationFn: async (v: { zustaendig: string | null; notiz?: string; wiedervorlage_am?: string | null;
                            wiedervorlage_loeschen?: boolean }) =>
      json<NotizenStand>(await fetch(`/api/v1/emails/${emailId}/zustaendig`, {
        method: "PUT", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(v),
      })),
    onSuccess: neu,
  })
}

/**
 * „Wartet auf Kunde" von Hand (#191, Marlene 15.10.2026): `wartet: true` nimmt
 * die Mail aus „Offen" und zeigt sie unter „Wartet auf Kunde" — bis der Kunde
 * antwortet. `wartet: false` ist „Wieder offen".
 */
export function useWartetSetzen(emailId: number) {
  const neu = useZusammenarbeitNeuLaden(emailId)
  return useMutation({
    mutationFn: async (wartet: boolean) =>
      json<NotizenStand>(await fetch(`/api/v1/emails/${emailId}/wartet`, {
        method: "PUT", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ wartet }),
      })),
    onSuccess: neu,
  })
}

// ── Personen ─────────────────────────────────────────────────────────────────

const FARBEN = ["#0F766E", "#3730A3", "#9D174D", "#92400E", "#1D4ED8", "#6D28D9", "#047857", "#B91C1C"]

export function kuerzel(name?: string | null): string {
  const n = (name || "").trim()
  if (!n) return "+"
  const teile = n.split(/[\s._-]+/).filter(Boolean)
  return ((teile[0]?.[0] || "") + (teile[1]?.[0] || teile[0]?.[1] || "")).toUpperCase()
}

export function personFarbe(name?: string | null): string {
  const n = (name || "").toLowerCase()
  let h = 0
  for (let i = 0; i < n.length; i++) h = (h * 31 + n.charCodeAt(i)) >>> 0
  return FARBEN[h % FARBEN.length]
}

/** Login-Name hübsch: „marlene" → „Marlene", „paul.lux" → „Paul Lux". */
export function anzeigeName(name?: string | null): string {
  return (name || "").split(/[._]/).filter(Boolean)
    .map((t) => t.charAt(0).toUpperCase() + t.slice(1)).join(" ")
}

/** ISO-Tag in N Tagen (Ortszeit). */
export function tagInTagen(n: number): string {
  const d = new Date()
  d.setDate(d.getDate() + n)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`
}

/** Nächster Freitag (heute Freitag → in einer Woche). */
export function naechsterFreitag(): string {
  const heute = new Date().getDay()
  const bis = ((5 - heute + 7) % 7) || 7
  return tagInTagen(bis)
}

export function tagKurz(iso?: string | null): string {
  if (!iso) return ""
  const [, m, t] = iso.slice(0, 10).split("-")
  return t && m ? `${t}.${m}.` : iso
}

/**
 * Tastenkürzel nur, wenn niemand gerade tippt und kein Dialog offen ist.
 * Sonst landet ein „e" aus einem Antworttext als „erledigt" an der Mail.
 */
export function tasteZaehlt(ev: KeyboardEvent): boolean {
  if (ev.defaultPrevented || ev.metaKey || ev.ctrlKey || ev.altKey) return false
  const z = ev.target as HTMLElement | null
  if (z && (z.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(z.tagName))) return false
  if (document.querySelector('[role="dialog"], [aria-modal="true"]')) return false
  return true
}
